const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

async function handleResponse<T>(res: Response): Promise<T> {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Request failed: ${res.status}`);
    }
    return data as T;
}

export type Space = {
    id: string;
    title: string;
    desc: string;
    by: string;
    members: string;
    online: string;
    category: string;
    accentColor?: string;
    isNew: boolean;
    isJoined: boolean;
    created: string;
    rules?: string[];
    admins?: string[];
    tags?: string[];
    isPublic?: boolean;
    constitution?: {
        purpose?: string;
        allowedContent?: string;
        appealProcess?: string;
        postPolicy?: {
            allowComments?: boolean;
            allowDislike?: boolean;
            allowLove?: boolean;
        };
    };
    accessMode?: "open" | "locked" | "private" | "sealed" | string;
    isLocked?: boolean;
    canView?: boolean;
    canEnter?: boolean;
    privateStats?: boolean;
    canManage?: boolean;
    canDeleteWithoutReview?: boolean;
    canDeleteDirectly?: boolean;
    canRequestDissolution?: boolean;
    deletionReviewRequired?: boolean;
    deletionUnavailableReason?: string;
    deletionLocked?: boolean;
    deletionLockedAt?: string | null;
    isArchived?: boolean;
    archivedAt?: string | null;
    archivedMemberCount?: number;
    archiveMessage?: string;
    dissolutionRequest?: {
        id: string;
        status: "pending" | "approved" | "rejected";
        reason: string;
        memberCount: number;
        reviewNote?: string;
        requestedAt: string;
        reviewedAt?: string | null;
    } | null;
    requiresInvite?: boolean;
    inviteCode?: string;
    inviteCodeHint?: string;
    inviteExpiresAt?: string | null;
    inviteExpired?: boolean;
    lockedReason?: string;
    accessGrants?: Array<{
        userId: string;
        username: string;
        mode: string;
        grantedAt?: string;
    }>;
    activity?: {
        posts: number;
        upcomingEvents: number;
        openReports: number;
        lastActiveAt: string | null;
    };
    creator?: string | null;
    coCreators?: string[];
    ownership?: {
        creator?: {
            id: string;
            username: string;
            displayName?: string;
            avatarUrl?: string;
        } | null;
        coCreators?: Array<{
            id: string;
            username: string;
            displayName?: string;
            avatarUrl?: string;
        }>;
    };
    createdAt?: string;
    updatedAt?: string;
};

export type SpaceMember = {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string;
    role: "Patron" | "Delegate" | "Admin" | "Creator" | "Member" | string;
    realmRoleLabel?: "Patron" | "Delegate" | "Member" | string;
    spaceRoleLabel?: "Creator" | "Co Owner" | "Admin" | "Member" | string;
    protected?: boolean;
    realmRole?: string;
    spaceRole?: "member" | "space_admin" | "creator" | "co_creator" | string;
    spaceStatus?: "active" | "muted" | "banned" | string;
    isMember?: boolean;
    status: "online" | "away" | "offline";
    joinedAt?: string;
};

export type SpaceInviteCandidate = {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string;
};

export type SpaceAuthority = {
    role: string;
    label: string;
    permissions: string[];
    isPresence?: boolean;
    isPatron?: boolean;
    isCreator?: boolean;
    isCoCreator?: boolean;
    realmRole?: string;
    spaceRole?: string;
    canModerate: boolean;
    canManageSpace?: boolean;
    canManageRoles: boolean;
    canAssignSpaceAdmin: boolean;
    canReviewReports: boolean;
    canRemovePosts: boolean;
    canRestrictUsers: boolean;
};

export type SpaceReport = {
    id: string;
    targetType: "post" | "comment" | "user" | "space";
    targetId: string;
    reason: string;
    details: string;
    status: "open" | "in_review" | "resolved" | "dismissed" | "escalated";
    severity: "low" | "medium" | "high";
    reporterUsername: string;
    handledByRole?: string;
    handledAt?: string | null;
    createdAt: string;
    updatedAt: string;
};

export type SpaceRestriction = {
    id: string;
    userId: string;
    username: string;
    displayName: string;
    avatarUrl: string;
    type: "muted" | "banned";
    reason: string;
    active: boolean;
    createdByRole: string;
    createdAt: string;
};

export type SpaceRoleRecord = {
    id: string;
    userId: string;
    username: string;
    displayName: string;
    avatarUrl: string;
    role: "space_admin";
    roleLabel: string;
    permissions: string[];
    active: boolean;
    grantedAt: string;
};

export type SpaceModeration = {
    authority: SpaceAuthority;
    members: SpaceMember[];
    roles: SpaceRoleRecord[];
    reports: SpaceReport[];
    restrictions: SpaceRestriction[];
    summary: {
        openReports: number;
        spaceAdmins: number;
        activeRestrictions: number;
    };
};

export type SpaceMaterial = {
    id: string;
    spaceId: string;
    kind: "material" | "assignment" | "reference" | "lecture" | "discussion";
    syncMode: "frozen" | "live";
    title: string;
    content: string;
    type: "text" | "code" | "list" | "rich";
    tags: string[];
    wordCount: number;
    permissions: {
        allowComments: boolean;
        allowCopy: boolean;
        allowRemix: boolean;
    };
    pinned: boolean;
    createdBy: {
        username: string;
        displayName?: string;
        avatarUrl?: string;
        deleted?: boolean;
    };
    canManage?: boolean;
    canSave?: boolean;
    createdAt: string;
    updatedAt: string;
};

export type FetchSpacesParams = {
    joined?: boolean;
    excludeJoined?: boolean;
    search?: string;
    category?: string;
    archived?: boolean | "any";
    accentColor?: string;
    profile?: string;
    page?: number;
    limit?: number;
};

export type SpaceNameAvailability = {
    available: boolean;
    title: string;
    titleKey: string;
    code: string;
    message: string;
    field?: string;
};

export async function fetchSpaces(params: FetchSpacesParams = {}): Promise<{
    spaces: Space[];
    total: number;
    page: number;
    hasMore: boolean;
}> {
    const url = new URL(`${API_BASE}/api/spaces`);
    if (params.joined !== undefined) url.searchParams.set("joined", String(params.joined));
    if (params.excludeJoined !== undefined) url.searchParams.set("excludeJoined", String(params.excludeJoined));
    if (params.search) url.searchParams.set("search", params.search);
    if (params.category && params.category !== "All") url.searchParams.set("category", params.category);
    if (params.archived !== undefined) url.searchParams.set("archived", String(params.archived));
    if (params.profile) url.searchParams.set("profile", params.profile);
    if (params.page) url.searchParams.set("page", String(params.page));
    if (params.limit) url.searchParams.set("limit", String(params.limit));

    const data = await handleResponse<any>(await fetch(url.toString(), { headers: authHeaders() }));
    return {
        spaces: data.spaces || [],
        total: data.total || 0,
        page: data.page || 1,
        hasMore: Boolean(data.hasMore),
    };
}

export async function fetchJoinedSpaces(params: Omit<FetchSpacesParams, "joined"> = {}) {
    return fetchSpaces({ ...params, joined: true });
}

export async function checkSpaceNameAvailability(
    title: string,
    options: { signal?: AbortSignal; excludeSpaceId?: string } = {}
): Promise<SpaceNameAvailability> {
    const url = new URL(`${API_BASE}/api/spaces/name-availability`);
    url.searchParams.set("title", title);
    if (options.excludeSpaceId) url.searchParams.set("excludeSpaceId", options.excludeSpaceId);
    const data = await handleResponse<any>(
        await fetch(url.toString(), {
            headers: authHeaders(),
            signal: options.signal,
        })
    );
    return {
        available: Boolean(data.available),
        title: data.title || "",
        titleKey: data.titleKey || "",
        code: data.code || "",
        message: data.message || "",
        field: data.field,
    };
}

export async function getSpace(spaceId: string): Promise<Space> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}`, {
            headers: authHeaders(),
        })
    );
    return data.space;
}

export async function createSpace(input: {
    title: string;
    desc?: string;
    category?: string;
    accentColor?: string;
    isPublic?: boolean;
    accessMode?: "open" | "locked" | "private" | "sealed" | string;
    inviteCode?: string;
    inviteExpiresAt?: string | null;
    constitution?: Space["constitution"];
    rules?: string[];
    tags?: string[];
}, options: { signal?: AbortSignal } = {}): Promise<Space> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(input),
            signal: options.signal,
        })
    );
    return data.space;
}

export async function updateSpace(spaceId: string, input: Partial<Space> & { regenerateInvite?: boolean; revokeExistingAccess?: boolean; lockReason?: string }): Promise<Space> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify(input),
        })
    );
    return data.space;
}

export async function addSpaceCoCreator(spaceId: string, input: { username?: string; userId?: string }): Promise<{ space: Space; message: string }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/co-creators`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(input),
        })
    );
    return { space: data.space, message: data.message || "Co-Owner added." };
}

export async function deleteSpace(spaceId: string): Promise<{ message: string }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}`, {
            method: "DELETE",
            headers: authHeaders(),
        })
    );
    return { message: data.message || "Space deleted." };
}

export async function joinSpace(spaceId: string): Promise<Space> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/join`, {
            method: "POST",
            headers: authHeaders(),
        })
    );
    return data.space;
}

export async function acceptSpaceInviteFromAlert(
    spaceId: string,
    notificationId: string
): Promise<{
    space: Space;
    message: string;
    unreadCount: number;
    notification?: { id: string; readAt?: string | null; metadata?: Record<string, any> };
}> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/invites/accept`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ notificationId }),
        })
    );
    return {
        space: data.space,
        message: data.message || "Joined Space.",
        unreadCount: Number(data.unreadCount || 0),
        notification: data.notification,
    };
}

export async function unlockSpace(spaceId: string, inviteCode: string): Promise<Space> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/unlock`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ inviteCode }),
        })
    );
    return data.space;
}

export async function leaveSpace(spaceId: string): Promise<Space> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/leave`, {
            method: "POST",
            headers: authHeaders(),
        })
    );
    return data.space;
}

export async function transferSpaceOwnership(spaceId: string, userId: string): Promise<{ space: Space; message: string }> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/transfer-ownership`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ userId }),
    }));
    return { space: data.space, message: data.message };
}

export async function requestSpaceDissolution(spaceId: string, reason: string): Promise<{ space?: Space; archived: boolean; message: string }> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/dissolution-requests`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ reason }),
    }));
    return { space: data.space, archived: Boolean(data.archived), message: data.message };
}

export async function reviewSpaceDissolution(spaceId: string, requestId: string, decision: "approved" | "rejected", note: string): Promise<{ space: Space; message: string }> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/dissolution-requests/${encodeURIComponent(requestId)}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ decision, note }),
    }));
    return { space: data.space, message: data.message };
}

export async function fetchSpaceMembers(spaceId: string): Promise<SpaceMember[]> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/members`, {
            headers: authHeaders(),
        })
    );
    return data.members || [];
}

export async function fetchSpaceAuthority(spaceId: string): Promise<SpaceAuthority | null> {
    const res = await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/authority`, {
        headers: authHeaders(),
    });
    if (res.status === 401 || res.status === 403 || res.status === 404) return null;
    const data = await handleResponse<any>(res);
    return data.authority || null;
}

export async function fetchSpaceModeration(spaceId: string, params: { q?: string; status?: "all" | "active" | "muted" | "banned" } = {}): Promise<SpaceModeration> {
    const url = new URL(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/moderation`);
    if (params.q) url.searchParams.set("q", params.q);
    if (params.status && params.status !== "all") url.searchParams.set("status", params.status);
    const data = await handleResponse<any>(
        await fetch(url.toString(), {
            headers: authHeaders(),
        })
    );
    return {
        authority: data.authority,
        members: data.members || [],
        roles: data.roles || [],
        reports: data.reports || [],
        restrictions: data.restrictions || [],
        summary: data.summary || {
            openReports: 0,
            spaceAdmins: 0,
            activeRestrictions: 0,
        },
    };
}

export async function fetchSpaceInviteCandidates(spaceId: string, query: string): Promise<SpaceInviteCandidate[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const url = new URL(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/invite-candidates`);
    url.searchParams.set("q", q);
    const data = await handleResponse<any>(
        await fetch(url.toString(), {
            headers: authHeaders(),
        })
    );
    return data.users || [];
}

export async function inviteSpaceUsers(spaceId: string, userIds: string[]): Promise<{ invited: SpaceInviteCandidate[]; message: string }> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/invites`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ userIds }),
        })
    );
    return {
        invited: data.invited || [],
        message: data.message || "Invite sent.",
    };
}

export type FetchSpaceMaterialsParams = {
    q?: string;
    kind?: SpaceMaterial["kind"] | "all";
    syncMode?: "live" | "frozen" | "all";
    sort?: "pinned" | "newest" | "oldest" | "title";
    page?: number;
    limit?: number;
    signal?: AbortSignal;
};

export type SpaceMaterialsResponse = {
    materials: SpaceMaterial[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
    nextPage: number | null;
};

export async function fetchSpaceMaterials(spaceId: string, params: FetchSpaceMaterialsParams = {}): Promise<SpaceMaterialsResponse> {
    const url = new URL(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/materials`);
    if (params.q?.trim()) url.searchParams.set("q", params.q.trim());
    if (params.kind && params.kind !== "all") url.searchParams.set("kind", params.kind);
    if (params.syncMode && params.syncMode !== "all") url.searchParams.set("syncMode", params.syncMode);
    if (params.sort) url.searchParams.set("sort", params.sort);
    if (params.page) url.searchParams.set("page", String(params.page));
    if (params.limit) url.searchParams.set("limit", String(params.limit));
    const data = await handleResponse<any>(
        await fetch(url.toString(), {
            headers: authHeaders(),
            signal: params.signal,
        })
    );
    return {
        materials: data.materials || [],
        total: Number(data.total ?? data.materials?.length ?? 0),
        page: Number(data.page || params.page || 1),
        limit: Number(data.limit || params.limit || 12),
        hasMore: Boolean(data.hasMore),
        nextPage: data.nextPage ?? null,
    };
}

export async function shareNoteToSpace(spaceId: string, input: {
    noteId: string;
    kind: SpaceMaterial["kind"];
    syncMode: "frozen" | "live";
    permissions: SpaceMaterial["permissions"];
}): Promise<SpaceMaterial> {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/materials`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(input),
        })
    );
    return data.material;
}

export async function saveSpaceMaterial(spaceId: string, materialId: string) {
    return handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/materials/${encodeURIComponent(materialId)}/save`, {
            method: "POST",
            headers: authHeaders(),
        })
    );
}

export async function removeSpaceMaterial(spaceId: string, materialId: string) {
    return handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/materials/${encodeURIComponent(materialId)}`, {
            method: "DELETE",
            headers: authHeaders(),
        })
    );
}

export async function updateSpaceRole(spaceId: string, userId: string, role: "member" | "space_admin", reason?: string) {
    return handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/roles/${encodeURIComponent(userId)}`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ role, reason }),
        })
    );
}

export async function createSpaceReport(spaceId: string, input: {
    targetType: "post" | "comment" | "user" | "space";
    targetId: string;
    reason: string;
    details?: string;
    severity?: "low" | "medium" | "high";
}) {
    return handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/reports`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(input),
        })
    );
}

export async function updateSpaceReport(spaceId: string, reportId: string, status: SpaceReport["status"]) {
    const data = await handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/reports/${encodeURIComponent(reportId)}`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ status }),
        })
    );
    return data.report as SpaceReport;
}

export async function restrictSpaceUser(spaceId: string, userId: string, input: { type: "muted" | "banned"; reason?: string }) {
    return handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/restrictions/${encodeURIComponent(userId)}`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(input),
        })
    );
}

export async function restoreSpaceUser(spaceId: string, userId: string) {
    return handleResponse<any>(
        await fetch(`${API_BASE}/api/spaces/${encodeURIComponent(spaceId)}/restrictions/${encodeURIComponent(userId)}`, {
            method: "DELETE",
            headers: authHeaders(),
        })
    );
}
